/**
 * PhotoboothHirePage ✦ iter217
 * ─────────────────────────────────────────────────────────────
 * The commercial sibling to the editorial memory platform.
 *
 * Sections (top to bottom):
 *   1. Hero — headline + subhead + coverage tagline + dual CTAs
 *      (View Packages · Check Availability).
 *   2. Packages — three cards, centre one (£350) visually featured
 *      with the "⭐ Most Popular & Most Booked" badge.
 *   3. Add-ons — Audio Guestbook · Premium Backdrop · LOVE Letters
 *      · Additional Prints.
 *   4. Booth Collection — Infinity Mirror (flagship) · Selfie Pod
 *      · Rustic Mirror · Traditional Enclosed.
 *   5. Coverage / SEO — region pills (Wrexham · North Wales ·
 *      Cheshire · Shropshire · Wirral · Liverpool · Manchester).
 *   6. Booking CTA strip — "Ready To Create Unforgettable Memories?".
 *
 * Booking flow per Craig (iter217): NO automatic payment. Every CTA
 * opens EnquiryForm; admin confirms availability + sends deposit
 * link manually afterwards. CTA wording rotates between
 * "Check Availability", "Request a Quote" and "Enquire Now" so the
 * page doesn't feel like a one-button transactional site.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ServicesNav from '../components/services/ServicesNav';
import BrandedPlaceholder from '../components/services/BrandedPlaceholder';
import EnquiryForm from '../components/services/EnquiryForm';
import DateAvailabilityChecker from '../components/DateAvailabilityChecker';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_GHOST, IVORY_FAINT,
  GOLD, GOLD_MUTED, GOLD_FAINT, GOLD_HAIRLINE,
  WALNUT_CARD, PAGE_BG, COVERAGE_REGIONS, COVERAGE_TAGLINE,
  PILL_BUTTON_PRIMARY, PILL_BUTTON_GHOST,
} from '../components/services/_tokens';
import FeaturedReviewsCarousel from '../components/FeaturedReviewsCarousel';
import SeenItAtBadge from '../components/SeenItAtBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PhotoboothHirePage = () => {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [booths, setBooths] = useState([]);
  const [backdrops, setBackdrops] = useState([]);
  // iter233 — Heritage Collection (admin-managed). Both products
  // and packages now come from /api/heritage/collection so Craig
  // can edit copy, photos and price without a redeploy.
  const [heritageProducts, setHeritageProducts] = useState([]);
  const [heritagePackages, setHeritagePackages] = useState([]);
  const [heritagePackagesVisible, setHeritagePackagesVisible] = useState(true);
  // iter220 — last 3 public events surface as social proof on the
  // commercial page. The section silently hides until at least one
  // event is published, so a fresh deploy never shows a half-empty
  // strip.
  const [recentEvents, setRecentEvents] = useState([]);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryPackage, setEnquiryPackage] = useState('');
  // iter224 — when a customer enquires after picking a date in the
  // calendar checker, pre-fill the form so they never retype it.
  const [enquiryDate, setEnquiryDate] = useState('');

  useEffect(() => {
    document.title = 'Photobooth Hire ✦ EventDrop — Wrexham, North Wales, Cheshire';
    // iter232 — Heritage Collection SEO. Inject the meta description
    // only when this page is mounted; restore the previous value on
    // unmount so other routes are unaffected.
    let prevMeta = null;
    let metaTag = document.querySelector('meta[name="description"]');
    const HERITAGE_META = "Discover EventDrop's Heritage Collection featuring rustic wedding photobooths, audio guestbooks, video guestbooks, wedding styling packages and handcrafted wedding décor.";
    if (metaTag) {
      prevMeta = metaTag.getAttribute('content');
      metaTag.setAttribute('content', HERITAGE_META);
    } else {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      metaTag.content = HERITAGE_META;
      document.head.appendChild(metaTag);
    }
    let cancelled = false;
    (async () => {
      try {
        const [pkgRes, boothRes, eventsRes, backdropsRes, heritageRes] = await Promise.all([
          axios.get(`${API}/services/photobooth/packages`),
          axios.get(`${API}/services/photobooth/booths`),
          axios.get(`${API}/public-events`),
          axios.get(`${API}/photobooth/backdrops`),
          axios.get(`${API}/heritage/collection`),
        ]);
        if (cancelled) return;
        setPackages(pkgRes.data.packages || []);
        setAddons(pkgRes.data.addons || []);
        setBooths(boothRes.data.booths || []);
        setRecentEvents((eventsRes.data.events || []).slice(0, 3));
        setBackdrops(backdropsRes.data.backdrops || []);
        setHeritageProducts(heritageRes.data.products || []);
        setHeritagePackages(heritageRes.data.packages || []);
        setHeritagePackagesVisible(heritageRes.data.packages_section_visible !== false);
      } catch {/* render empty + retry next mount */}
    })();
    return () => {
      cancelled = true;
      if (prevMeta !== null) {
        const m = document.querySelector('meta[name="description"]');
        if (m) m.setAttribute('content', prevMeta);
      }
    };
  }, []);

  const openEnquiry = (slug = '', dateIso = '') => {
    setEnquiryPackage(slug);
    setEnquiryDate(dateIso);
    setEnquiryOpen(true);
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="photobooth-hire-page" style={{
      minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF,
    }}>
      {/* SEO microdata for LocalBusiness — helps Google show coverage areas */}
      <SEOJsonLd />
      <ServicesNav active="services" />
      <Hero openEnquiry={openEnquiry} scrollTo={scrollTo} />
      <HeritageCollection
        products={heritageProducts}
        packages={heritagePackages}
        packagesVisible={heritagePackagesVisible}
        openEnquiry={openEnquiry}
      />
      <PackagesSection packages={packages} openEnquiry={openEnquiry} />
      <AddonsSection addons={addons} openEnquiry={openEnquiry} />
      <BackdropsSection backdrops={backdrops} openEnquiry={openEnquiry} />
      <BoothCollection booths={booths} openEnquiry={openEnquiry} />
      <FeaturedReviewsCarousel
        variant="cream"
        filter="product_page"
        eyebrow="✦ Loved by past customers"
      />
      <RecentlyCaptured events={recentEvents} />
      <SocialProof />
      <CoverageStrip />
      <BookingCTA openEnquiry={openEnquiry} />
      <Footer />
      {enquiryOpen && (
        <EnquiryForm
          packages={packages}
          addons={addons}
          initialPackageSlug={enquiryPackage}
          initialEventDate={enquiryDate}
          onClose={() => setEnquiryOpen(false)}
          sourcePage="photobooth-hire"
        />
      )}
    </div>
  );
};

// ─── SEO microdata ─────────────────────────────────────────────────
const SEOJsonLd = () => {
  const data = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'EventDrop Photobooth Hire',
    description:
      'Photobooth hire across Wrexham, North Wales, Cheshire, Shropshire and the North West. ' +
      'Infinity Mirror, Rustic Mirror, Selfie Pod and Traditional Enclosed booths for weddings, proms and parties.',
    areaServed: COVERAGE_REGIONS.map(r => ({ '@type': 'Place', name: r })),
    priceRange: '£200 – £500',
    url: 'https://eventdrop.co/photobooth-hire',
  }), []);
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
};

// ─── Hero ──────────────────────────────────────────────────────────
const Hero = ({ openEnquiry, scrollTo }) => (
  <section
    data-testid="photobooth-hero"
    style={{
      padding: 'clamp(72px, 12vw, 130px) clamp(20px, 5vw, 60px) clamp(56px, 8vw, 90px)',
      textAlign: 'center', position: 'relative', overflow: 'hidden',
    }}
  >
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: GOLD_MUTED, marginBottom: 22,
      }} data-testid="photobooth-eyebrow">
        ✦ Photobooth Hire · Event Services
      </div>
      <h1 style={{
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 'clamp(36px, 6vw, 64px)', lineHeight: 1.08,
        letterSpacing: '-0.02em', color: IVORY, margin: 0,
        textShadow: '0 0 40px rgba(212,170,82,0.18)',
      }} data-testid="photobooth-headline">
        {COVERAGE_TAGLINE}
      </h1>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.6vw, 19px)',
        color: IVORY_DIM, maxWidth: 720, margin: '26px auto 0',
        lineHeight: 1.6,
      }} data-testid="photobooth-subhead">
        Create unforgettable memories with premium photo experiences, instant prints, audio guestbooks and event enhancements.
      </p>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 36 }}>
        <button
          onClick={() => scrollTo('packages')}
          data-testid="hero-view-packages"
          style={PILL_BUTTON_PRIMARY}
        >View Packages ✦</button>
        <button
          onClick={() => openEnquiry('')}
          data-testid="hero-check-availability"
          style={PILL_BUTTON_GHOST}
        >Check Availability</button>
      </div>
      {/* iter224 — Date-based availability checker replaces the
          generic stock counter as the primary hero widget. Still
          renders the iter223 fallback strip beneath itself when no
          date has been picked yet. */}
      <div style={{ marginTop: 44 }}>
        <DateAvailabilityChecker onEnquire={(iso) => openEnquiry('', iso)} />
      </div>
      {/* Rotating booth preview row — branded placeholders until real photos land */}
      <div style={{
        marginTop: 60,
        display: 'grid', gap: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      }} data-testid="hero-booth-preview-row">
        {['Infinity Mirror', 'Rustic Mirror', 'Selfie Pod', 'Traditional Enclosed', 'Audio Guestbook', 'Rustic 4ft LOVE Letters', 'Flower Walls', 'Shimmering Sequin Backdrops']
          .map((label, i) => (
            <div key={label} style={{ opacity: 0, animation: `phbFade 700ms ease ${i * 90}ms forwards` }}>
              <BrandedPlaceholder label={label} aspectRatio="3 / 4" testId={`hero-booth-${i}`} />
            </div>
          ))}
      </div>
    </div>
    <style>{`@keyframes phbFade { to { opacity: 1; } }`}</style>
  </section>
);

// ─── The Heritage Collection (iter232 · admin-managed iter233) ─────
// Premium flagship band positioned directly after the Hero, ahead
// of the standard photobooth catalogue. Breaks the dark page with
// a light cream palette (per Craig's spec) to signal a curated
// premium experience — barn / woodland / country house weddings.
//
// Products + packages are now admin-managed via /admin/heritage
// so Craig can edit copy, swap photos, flip Coming-Soon → Live and
// add brand-new items without a redeploy.
const HERITAGE = {
  bg: '#F8F5EF',          // background — soft chalk
  primary: '#7A5C3E',     // walnut (text + accents)
  secondary: '#B38B59',   // warm tan (borders + price)
  accent: '#556B2F',      // olive (badges)
  text: '#2E2E2E',        // body copy
  textDim: '#6B5E4F',     // secondary body copy
  cardBg: '#FFFFFF',
  cardShadow: '0 18px 60px rgba(75,55,30,0.12)',
};

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const HeritageCollection = ({ products, packages, packagesVisible = true, openEnquiry }) => {
  if ((!products || products.length === 0) && (!packages || packages.length === 0)) return null;
  const heroProduct = products?.find(p => p.image_url) || products?.[0];
  const heroImage = heroProduct?.image_url ? mediaUrl(heroProduct.image_url) : null;
  return (
  <section
    data-testid="heritage-collection"
    style={{
      background: HERITAGE.bg,
      color: HERITAGE.text,
      padding: 'clamp(70px, 10vw, 120px) clamp(20px, 5vw, 60px)',
      borderTop: `4px solid ${HERITAGE.secondary}`,
      borderBottom: `4px solid ${HERITAGE.secondary}`,
    }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* Eyebrow */}
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: HERITAGE.secondary, textAlign: 'center', marginBottom: 16,
      }}>✦ A Signature Collection</div>

      {/* Heading */}
      <h2 style={{
        fontFamily: SERIF, fontWeight: 500, textAlign: 'center',
        fontSize: 'clamp(34px, 5.5vw, 56px)', letterSpacing: '-0.025em',
        color: HERITAGE.primary, margin: '0 0 22px',
        lineHeight: 1.05,
      }}>The Heritage Collection</h2>

      {/* Subheading */}
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.6vw, 18px)',
        color: HERITAGE.textDim, textAlign: 'center', maxWidth: 760,
        margin: '0 auto 18px', lineHeight: 1.7,
      }}>
        A carefully curated range of rustic wedding experiences, handcrafted styling pieces and interactive guest entertainment designed to create timeless memories.
      </p>

      {/* Perfect for chips */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
        justifyContent: 'center', marginBottom: 60,
      }}>
        {['Barn Weddings', 'Woodland Weddings', 'Country House Weddings', 'Rustic Celebrations', 'Luxury Outdoor Weddings'].map(c => (
          <span key={c} data-testid={`heritage-chip-${c.toLowerCase().replace(/\s+/g, '-')}`} style={{
            fontFamily: SERIF, fontSize: 12.5, letterSpacing: '0.04em',
            padding: '6px 14px', borderRadius: 999,
            background: 'rgba(122,92,62,0.06)', border: `1px solid rgba(122,92,62,0.18)`,
            color: HERITAGE.primary,
          }}>{c}</span>
        ))}
      </div>

      {/* Hero showcase card */}
      {heroImage && (
        <HeritageHeroCard heroImage={heroImage} products={products} openEnquiry={openEnquiry} />
      )}

      {/* SEO keywords (visually hidden but indexed) */}
      <div style={{ position: 'absolute', left: -9999, top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="false">
        <h3>Rustic Wedding Photobooth Hire</h3>
        <h3>Woodland Wedding Styling</h3>
        <h3>Wedding Audio Guestbook</h3>
        <h3>Wedding Video Guestbook</h3>
        <h3>Rustic Wedding Packages</h3>
      </div>

      {/* Products grid */}
      {products && products.length > 0 && (
        <div style={{ marginTop: 70 }}>
          <h3 style={{
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(22px, 2.6vw, 30px)', textAlign: 'center',
            color: HERITAGE.primary, margin: '0 0 8px', letterSpacing: '-0.015em',
          }}>Heritage Products</h3>
          <p style={{
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
            color: HERITAGE.textDim, textAlign: 'center', margin: '0 0 36px',
          }}>Available now — enquire for availability on your date.</p>
          <div data-testid="heritage-products-grid" style={{
            display: 'grid', gap: 22,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}>
            {products.map(p => (
              <HeritageProductCard key={p.slug} product={p} openEnquiry={openEnquiry} />
            ))}
          </div>
        </div>
      )}

      {/* Packages */}
      {packagesVisible && packages && packages.length > 0 && (
        <div style={{ marginTop: 80 }}>
          <h3 style={{
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(22px, 2.6vw, 30px)', textAlign: 'center',
            color: HERITAGE.primary, margin: '0 0 8px', letterSpacing: '-0.015em',
          }}>Heritage Collection Packages</h3>
          <p style={{
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
            color: HERITAGE.textDim, textAlign: 'center', margin: '0 0 36px',
          }}>Complete styling experiences launching soon.</p>
          <div data-testid="heritage-packages-grid" style={{
            display: 'grid', gap: 22,
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}>
            {packages.map(pkg => (
              <HeritagePackageCard key={pkg.slug} pkg={pkg} openEnquiry={openEnquiry} />
            ))}
          </div>
        </div>
      )}
    </div>
  </section>
  );
};

const HeritageHeroCard = ({ heroImage, products, openEnquiry }) => (
  <div data-testid="heritage-hero-card" style={{
    background: HERITAGE.cardBg,
    borderRadius: 14,
    boxShadow: HERITAGE.cardShadow,
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 1fr) minmax(280px, 1fr)',
    border: `1px solid rgba(122,92,62,0.14)`,
  }}>
    <div style={{
      backgroundImage: `url(${heroImage})`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      minHeight: 380,
    }} aria-label="Heritage Collection feature image" />
    <div style={{ padding: 'clamp(28px, 4vw, 48px)', position: 'relative' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: HERITAGE.accent, color: '#F8F5EF',
        padding: '5px 12px', borderRadius: 999,
        fontFamily: SERIF, fontSize: 11, letterSpacing: '0.18em',
        textTransform: 'uppercase', marginBottom: 18,
      }} data-testid="heritage-signature-badge">
        🏆 Signature Collection
      </div>
      <h3 style={{
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 'clamp(26px, 3vw, 36px)',
        color: HERITAGE.primary, margin: '0 0 6px',
        letterSpacing: '-0.02em', lineHeight: 1.15,
      }}>The Heritage Collection</h3>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 15, color: HERITAGE.secondary,
        margin: '0 0 22px', letterSpacing: '0.02em',
      }}>Timeless rustic wedding experiences</p>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px' }}>
        {(products || []).map(p => (
          <li key={p.slug} style={{
            fontFamily: SERIF, fontSize: 14.5,
            color: HERITAGE.text, padding: '7px 0',
            borderBottom: '1px dashed rgba(122,92,62,0.14)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ color: HERITAGE.accent, fontSize: 13 }}>✓</span>
            {p.name}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => openEnquiry('')}
        data-testid="heritage-hero-cta"
        style={{
          background: HERITAGE.primary, color: '#F8F5EF',
          border: 'none', borderRadius: 999,
          padding: '12px 28px', fontFamily: SERIF, fontSize: 14,
          letterSpacing: '0.04em', cursor: 'pointer',
          transition: 'transform 180ms ease, background 180ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = HERITAGE.secondary; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = HERITAGE.primary; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        Enquire about the Heritage Collection →
      </button>
    </div>
    <style>{`
      @media (max-width: 760px) {
        [data-testid="heritage-hero-card"] {
          grid-template-columns: 1fr !important;
        }
      }
    `}</style>
  </div>
);

const HeritageProductCard = ({ product, openEnquiry }) => {
  const cover = product.image_url ? mediaUrl(product.image_url) : null;
  const isFeatured = Boolean(product.is_featured);
  const priceFrom = Number(product.price_from) > 0 ? Number(product.price_from) : null;
  return (
  <div
    data-testid={`heritage-product-${product.slug}`}
    data-featured={isFeatured ? 'true' : 'false'}
    style={{
      background: HERITAGE.cardBg,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: isFeatured
        ? '0 28px 80px rgba(156, 124, 46, 0.28), 0 0 0 1px rgba(156, 124, 46, 0.35) inset'
        : HERITAGE.cardShadow,
      border: isFeatured
        ? `2px solid ${HERITAGE.accent}`
        : `1px solid rgba(122,92,62,0.14)`,
      display: 'flex',
      flexDirection: isFeatured ? 'row' : 'column',
      transition: 'transform 220ms ease, box-shadow 220ms ease',
      gridColumn: isFeatured ? '1 / -1' : 'auto',
      position: 'relative',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = isFeatured
        ? '0 36px 100px rgba(156, 124, 46, 0.38), 0 0 0 1px rgba(156, 124, 46, 0.45) inset'
        : '0 24px 70px rgba(75,55,30,0.18)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = isFeatured
        ? '0 28px 80px rgba(156, 124, 46, 0.28), 0 0 0 1px rgba(156, 124, 46, 0.35) inset'
        : HERITAGE.cardShadow;
    }}
  >
    {isFeatured && (
      <span
        data-testid={`heritage-product-featured-${product.slug}`}
        style={{
          position: 'absolute', top: 18, left: 18, zIndex: 2,
          background: HERITAGE.accent, color: '#FFF8E7',
          fontFamily: SERIF, fontSize: 10.5,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          padding: '6px 14px', borderRadius: 999,
          boxShadow: '0 6px 18px rgba(75,55,30,0.35)',
        }}
      >★ Featured Product</span>
    )}
    {cover ? (
      <div style={{
        backgroundImage: `url(${cover})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        aspectRatio: isFeatured ? 'auto' : '4 / 3',
        width: isFeatured ? '52%' : '100%',
        minHeight: isFeatured ? 420 : undefined,
      }} />
    ) : (
      <div style={{
        aspectRatio: isFeatured ? 'auto' : '4 / 3',
        width: isFeatured ? '52%' : '100%',
        minHeight: isFeatured ? 420 : undefined,
        background: `linear-gradient(135deg, rgba(122,92,62,0.10), rgba(179,139,89,0.18))`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: HERITAGE.secondary, fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, letterSpacing: '0.06em',
      }}>Photography coming soon</div>
    )}
    <div style={{
      padding: isFeatured ? 'clamp(28px, 3.5vw, 44px)' : '22px 22px 24px',
      display: 'flex', flexDirection: 'column', flex: 1,
      justifyContent: isFeatured ? 'center' : 'flex-start',
    }}>
      {isFeatured && (
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11.5, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: HERITAGE.accent, margin: '0 0 10px',
        }}>✦ New Signature Booth</div>
      )}
      <h4 style={{
        fontFamily: SERIF, fontWeight: 500,
        fontSize: isFeatured ? 'clamp(26px, 3vw, 38px)' : 19,
        color: HERITAGE.primary,
        margin: '0 0 10px', letterSpacing: '-0.015em',
        lineHeight: 1.15,
      }}>{product.name}</h4>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: isFeatured ? 15 : 13.5,
        color: HERITAGE.textDim,
        margin: '0 0 16px', lineHeight: 1.65,
      }}>{product.description}</p>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', flex: isFeatured ? '0 0 auto' : 1 }}>
        {(product.features || []).map(f => (
          <li key={f} style={{
            fontFamily: SERIF, fontSize: isFeatured ? 14 : 13,
            color: HERITAGE.text,
            padding: '4px 0', display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <span style={{ color: HERITAGE.accent, fontSize: 11 }}>●</span>
            {f}
          </li>
        ))}
      </ul>
      {priceFrom && (
        <div
          data-testid={`heritage-product-price-${product.slug}`}
          style={{
            fontFamily: SERIF, fontSize: 14,
            color: HERITAGE.primary, letterSpacing: '0.04em',
            margin: '0 0 18px',
          }}
        >
          From <span style={{ fontSize: isFeatured ? 28 : 20, fontWeight: 500, color: HERITAGE.accent }}>£{priceFrom}</span>
        </div>
      )}
      {isFeatured && (
        <div style={{ margin: '0 0 18px' }}>
          <SeenItAtBadge package={product.slug} variant="cream" eventLabel="real weddings &amp; events" />
        </div>
      )}
      {isFeatured ? (
        <button
          type="button"
          onClick={() => openEnquiry('')}
          data-testid={`heritage-product-cta-${product.slug}`}
          style={{
            background: HERITAGE.accent, color: '#FFF8E7',
            border: 'none', borderRadius: 999,
            padding: '14px 26px',
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 13, letterSpacing: '0.14em',
            textTransform: 'uppercase',
            cursor: 'pointer', alignSelf: 'flex-start',
            boxShadow: '0 12px 30px rgba(156, 124, 46, 0.32)',
            transition: 'transform 180ms ease, box-shadow 180ms ease, background 180ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.background = HERITAGE.primary;
            e.currentTarget.style.boxShadow = '0 18px 40px rgba(75,55,30,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.background = HERITAGE.accent;
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(156, 124, 46, 0.32)';
          }}
        >
          Enquire NOW for availability · Booking Fast
        </button>
      ) : (
        <button
          type="button"
          onClick={() => openEnquiry('')}
          data-testid={`heritage-product-cta-${product.slug}`}
          style={{
            background: 'transparent', color: HERITAGE.primary,
            border: `1px solid ${HERITAGE.secondary}`,
            borderRadius: 999, padding: '9px 18px',
            fontFamily: SERIF, fontSize: 13, letterSpacing: '0.04em',
            cursor: 'pointer', alignSelf: 'flex-start',
            transition: 'all 180ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = HERITAGE.primary; e.currentTarget.style.color = '#F8F5EF'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HERITAGE.primary; }}
        >
          View Details →
        </button>
      )}
    </div>
    {/* iter236c — featured card stacks on mobile so the booth image
        sits above the copy instead of stealing half the width. */}
    <style>{`
      @media (max-width: 760px) {
        [data-testid="heritage-product-${product.slug}"][data-featured="true"] {
          flex-direction: column !important;
        }
        [data-testid="heritage-product-${product.slug}"][data-featured="true"] > div:first-of-type {
          width: 100% !important;
          aspect-ratio: 4 / 3 !important;
          min-height: 0 !important;
        }
      }
    `}</style>
  </div>
  );
};

const HeritagePackageCard = ({ pkg, openEnquiry }) => {
  const isLive = pkg.status === 'live';
  return (
  <div
    data-testid={`heritage-package-${pkg.slug}`}
    style={{
      background: HERITAGE.cardBg,
      borderRadius: 12,
      padding: '24px 22px',
      border: `1px solid rgba(122,92,62,0.14)`,
      boxShadow: HERITAGE.cardShadow,
      display: 'flex', flexDirection: 'column',
      position: 'relative',
      opacity: isLive ? 1 : 0.92,
    }}
  >
    <div style={{
      position: 'absolute', top: 12, right: 12,
      background: isLive ? HERITAGE.accent : 'rgba(122,92,62,0.10)',
      color: isLive ? '#F8F5EF' : HERITAGE.primary,
      padding: '4px 10px', borderRadius: 999,
      fontFamily: SERIF, fontSize: 10.5, letterSpacing: '0.16em',
      textTransform: 'uppercase',
    }}>{isLive ? '✦ Available' : '🚧 Coming Soon'}</div>
    <h4 style={{
      fontFamily: SERIF, fontWeight: 500, fontSize: 18,
      color: HERITAGE.primary, margin: '0 0 14px',
      letterSpacing: '-0.01em', maxWidth: 'calc(100% - 130px)',
    }}>{pkg.name}</h4>
    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', flex: 1 }}>
      {(pkg.includes || []).map(item => (
        <li key={item} style={{
          fontFamily: SERIF, fontSize: 13, color: HERITAGE.text,
          padding: '4px 0', display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <span style={{ color: HERITAGE.accent, fontSize: 11 }}>✓</span>
          {item}
        </li>
      ))}
    </ul>
    <div style={{
      display: 'flex', alignItems: 'baseline',
      justifyContent: 'space-between', gap: 10,
    }}>
      <span style={{
        fontFamily: SERIF, fontWeight: 600, fontSize: 26,
        color: HERITAGE.primary, letterSpacing: '-0.01em',
      }}>{pkg.price ? `£${pkg.price}` : 'On request'}</span>
      <button type="button"
        disabled={!isLive}
        onClick={() => isLive && openEnquiry && openEnquiry('')}
        data-testid={`heritage-package-cta-${pkg.slug}`}
        style={{
          background: isLive ? HERITAGE.primary : 'transparent',
          color: isLive ? '#F8F5EF' : 'rgba(122,92,62,0.42)',
          border: isLive ? 'none' : '1px solid rgba(122,92,62,0.18)',
          borderRadius: 999, padding: '8px 16px',
          fontFamily: SERIF, fontSize: 12.5, letterSpacing: '0.04em',
          cursor: isLive ? 'pointer' : 'not-allowed',
        }}>{isLive ? 'Enquire →' : 'Notify me'}</button>
    </div>
  </div>
  );
};


// ─── Packages ──────────────────────────────────────────────────────
const PackagesSection = ({ packages, openEnquiry }) => (
  <section
    id="packages"
    data-testid="photobooth-packages"
    style={{ padding: 'clamp(70px, 10vw, 110px) clamp(20px, 5vw, 60px)', borderTop: `1px solid ${GOLD_HAIRLINE}` }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <SectionEyebrow>✦ The Packages</SectionEyebrow>
      <SectionHeading>Capture the night your way</SectionHeading>

      <div style={{
        display: 'grid', gap: 22, marginTop: 50,
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        alignItems: 'stretch',
      }}>
        {packages.map(p => (
          <PackageCard key={p.slug} pkg={p} onEnquire={() => openEnquiry(p.slug)} />
        ))}
      </div>
    </div>
  </section>
);

const PackageCard = ({ pkg, onEnquire }) => {
  const isFeatured = !!pkg.featured;
  // iter222f — admin-uploaded cover image. Falls back to no image
  // (text-led design) when the admin hasn't uploaded one yet.
  const coverUrl = pkg.image_url
    ? (pkg.image_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${pkg.image_url}` : pkg.image_url)
    : null;
  return (
    <div
      data-testid={`pkg-card-${pkg.slug}`}
      data-featured={isFeatured ? 'true' : 'false'}
      style={{
        position: 'relative',
        background: WALNUT_CARD,
        border: `1px solid ${isFeatured ? GOLD : GOLD_HAIRLINE}`,
        borderRadius: 12,
        padding: 0,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: isFeatured
          ? '0 24px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,170,82,0.22) inset, 0 0 60px rgba(212,170,82,0.10)'
          : '0 16px 50px rgba(0,0,0,0.4), inset 0 1px 0 rgba(212,170,82,0.06)',
        transform: isFeatured ? 'translateY(-6px)' : 'translateY(0)',
      }}
    >
      {isFeatured && (
        <div data-testid={`pkg-badge-${pkg.slug}`} style={{
          position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
          padding: '6px 16px', borderRadius: 999,
          background: `linear-gradient(135deg, #E8C175 0%, ${GOLD} 100%)`,
          color: '#0F0B1A',
          fontFamily: SERIF, fontWeight: 700, fontSize: 11.5,
          letterSpacing: '0.20em', textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          boxShadow: '0 8px 22px rgba(0,0,0,0.45)',
          zIndex: 2,
        }}>{pkg.badge || '⭐ Most Popular'}</div>
      )}
      {coverUrl && (
        <img
          data-testid={`pkg-image-${pkg.slug}`}
          src={coverUrl}
          alt={pkg.name}
          style={{
            width: '100%', aspectRatio: '4 / 3',
            objectFit: 'cover', display: 'block',
            borderBottom: `1px solid ${GOLD_HAIRLINE}`,
          }}
        />
      )}
      <div style={{
        position: 'relative',
        padding: isFeatured && !coverUrl
          ? 'clamp(54px, 4vw, 62px) 26px clamp(26px, 3vw, 36px)'
          : 'clamp(26px, 3vw, 36px) 26px',
        display: 'flex', flexDirection: 'column', flex: 1,
      }}>
      <h3 style={{
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 24, color: IVORY, margin: '0 0 6px',
        letterSpacing: '-0.005em',
      }}>{pkg.name}</h3>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13.5, color: IVORY_GHOST, margin: '0 0 22px',
        lineHeight: 1.5,
      }}>{pkg.tagline}</p>
      <div style={{
        fontFamily: SERIF, fontWeight: 600,
        fontSize: 44, color: IVORY, marginBottom: 22,
        letterSpacing: '-0.01em',
      }}>£{pkg.price}</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 28, flex: 1 }}>
        {(pkg.inclusions || []).map((inc, i) => (
          <li key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            fontFamily: SERIF, fontSize: 14, color: IVORY,
            lineHeight: 1.6, marginBottom: 8,
          }}>
            <span style={{ color: GOLD, flexShrink: 0, marginTop: 2 }}>✦</span>
            <span>{inc}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onEnquire}
        data-testid={`pkg-cta-${pkg.slug}`}
        style={isFeatured ? PILL_BUTTON_PRIMARY : { ...PILL_BUTTON_GHOST, justifyContent: 'center' }}
      >
        {isFeatured ? 'Request a Quote ✦' : 'Enquire Now'}
      </button>
      </div>
    </div>
  );
};

// ─── Add-ons ───────────────────────────────────────────────────────
const AddonsSection = ({ addons, openEnquiry }) => (
  <section
    data-testid="photobooth-addons"
    style={{ padding: 'clamp(70px, 10vw, 110px) clamp(20px, 5vw, 60px)', borderTop: `1px solid ${GOLD_HAIRLINE}` }}
  >
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <SectionEyebrow>✦ Add-ons</SectionEyebrow>
      <SectionHeading>Quiet upgrades to the night</SectionHeading>
      <SectionLede>
        Pair any package with these signature additions. We&apos;ll confirm pricing and availability with your quote.
      </SectionLede>

      <div style={{
        display: 'grid', gap: 18, marginTop: 50,
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      }}>
        {addons.map(a => {
          const addonCover = a.image_url
            ? (a.image_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${a.image_url}` : a.image_url)
            : null;
          return (
          <div
            key={a.slug}
            data-testid={`addon-card-${a.slug}`}
            style={{
              background: WALNUT_CARD,
              border: `1px solid ${GOLD_HAIRLINE}`,
              borderRadius: 10,
              padding: 0,
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            }}
          >
            {addonCover && (
              <img
                data-testid={`addon-image-${a.slug}`}
                src={addonCover}
                alt={a.name}
                style={{
                  width: '100%', aspectRatio: '4 / 3',
                  objectFit: 'cover', display: 'block',
                  borderBottom: `1px solid ${GOLD_HAIRLINE}`,
                }}
              />
            )}
            <div style={{ padding: '24px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <h4 style={{
              fontFamily: SERIF, fontWeight: 500, fontSize: 18,
              color: IVORY, margin: '0 0 4px', letterSpacing: '-0.005em',
            }}>{a.name}</h4>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13, color: IVORY_DIM, margin: '0 0 18px',
              lineHeight: 1.55, flex: 1,
            }}>{a.description}</p>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            }}>
              <span style={{
                fontFamily: SERIF, fontWeight: 600,
                fontSize: 22, color: GOLD, letterSpacing: '-0.01em',
              }}>
                {a.price ? `£${a.price}` : 'On request'}
              </span>
              <button
                type="button"
                onClick={() => openEnquiry('')}
                data-testid={`addon-cta-${a.slug}`}
                style={{
                  background: 'transparent', border: 'none',
                  color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12.5, letterSpacing: '0.06em',
                  cursor: 'pointer', textDecoration: 'underline',
                  textDecorationColor: GOLD_FAINT,
                  textUnderlineOffset: 4,
                }}
              >Add to enquiry →</button>
            </div>
            </div>
          </div>
        );})}
      </div>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12.5, color: IVORY_FAINT, textAlign: 'center',
        margin: '30px 0 0', letterSpacing: '0.02em',
      }}>
        Additional travel charges may apply outside the standard coverage area.
      </p>
    </div>
  </section>
);

// ─── Backdrop Hire (iter230) ───────────────────────────────────────
// Now admin-managed via /admin/backdrops — Craig can edit prices,
// upload real photos, reorder, hide and add new backdrops without a
// redeploy. The customer surface auto-hides if all are deactivated.
const BackdropsSection = ({ backdrops, openEnquiry }) => {
  if (!backdrops || backdrops.length === 0) return null;
  return (
    <section
      data-testid="photobooth-backdrops"
      style={{ padding: 'clamp(70px, 10vw, 110px) clamp(20px, 5vw, 60px)', borderTop: `1px solid ${GOLD_HAIRLINE}` }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <SectionEyebrow>✦ Backdrop Hire</SectionEyebrow>
        <SectionHeading>Hire the wall on its own</SectionHeading>
        <SectionLede>
          Each of our backdrops can be hired separately for venue dressing, content creation or as the centrepiece of your own photo wall. We deliver, set up and collect.
        </SectionLede>

        <div style={{
          display: 'grid', gap: 18, marginTop: 50,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}>
          {backdrops.map(b => {
            const cover = b.image_url
              ? (b.image_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${b.image_url}` : b.image_url)
              : null;
            return (
            <div
              key={b.slug}
              data-testid={`backdrop-card-${b.slug}`}
              style={{
                background: WALNUT_CARD,
                border: `1px solid ${GOLD_HAIRLINE}`,
                borderRadius: 10,
                padding: 0,
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span aria-hidden="true" style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg, transparent, ${b.accent}, transparent)`,
                zIndex: 2,
              }} />
              {cover && (
                <img
                  data-testid={`backdrop-image-${b.slug}`}
                  src={cover}
                  alt={b.name}
                  style={{
                    width: '100%', aspectRatio: '4 / 3',
                    objectFit: 'cover', display: 'block',
                    borderBottom: `1px solid ${GOLD_HAIRLINE}`,
                  }}
                />
              )}
              <div style={{ padding: '24px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <h4 style={{
                  fontFamily: SERIF, fontWeight: 500, fontSize: 17.5,
                  color: IVORY, margin: '4px 0 6px', letterSpacing: '-0.005em',
                  lineHeight: 1.3,
                }}>{b.name}</h4>
                <p style={{
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 13, color: IVORY_DIM, margin: '0 0 18px',
                  lineHeight: 1.55, flex: 1,
                }}>{b.description}</p>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                }}>
                  <span style={{
                    fontFamily: SERIF, fontWeight: 600,
                    fontSize: 18, color: GOLD, letterSpacing: '-0.01em',
                  }}>
                    {b.price ? `£${b.price}` : 'On request'}
                  </span>
                  <button
                    type="button"
                    onClick={() => openEnquiry('')}
                    data-testid={`backdrop-cta-${b.slug}`}
                    style={{
                      background: 'transparent', border: 'none',
                      color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 12.5, letterSpacing: '0.06em',
                      cursor: 'pointer', textDecoration: 'underline',
                      textDecorationColor: GOLD_FAINT,
                      textUnderlineOffset: 4,
                    }}
                  >Add to enquiry →</button>
                </div>
              </div>
            </div>
          );})}
        </div>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: IVORY_FAINT, textAlign: 'center',
          margin: '30px 0 0', letterSpacing: '0.02em',
        }}>
          Backdrops can also be added to any of the packages above at a reduced rate.
        </p>
      </div>
    </section>
  );
};

// ─── Booth Collection ──────────────────────────────────────────────
const BoothCollection = ({ booths, openEnquiry }) => (
  <section
    data-testid="photobooth-booths"
    style={{ padding: 'clamp(70px, 10vw, 110px) clamp(20px, 5vw, 60px)', borderTop: `1px solid ${GOLD_HAIRLINE}` }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <SectionEyebrow>✦ The Booth Collection</SectionEyebrow>
      <SectionHeading>Choose the booth that suits your night</SectionHeading>
      <SectionLede>
        Every booth includes setup, collection, full-evening attendant-free running and an online gallery.
      </SectionLede>

      <div style={{
        display: 'grid', gap: 20, marginTop: 50,
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      }}>
        {booths.map((b) => (
          <div key={b.slug} data-testid={`booth-card-${b.slug}`} style={{
            background: WALNUT_CARD,
            border: `1px solid ${b.coming_soon ? GOLD : (b.flagship ? GOLD : GOLD_HAIRLINE)}`,
            borderRadius: 12,
            padding: 0, overflow: 'hidden',
            boxShadow: '0 16px 50px rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column',
            position: 'relative',
          }}>
            {b.coming_soon && (
              <span
                data-testid={`booth-coming-soon-${b.slug}`}
                style={{
                  position: 'absolute', top: 14, left: 14, zIndex: 2,
                  background: GOLD, color: '#1A120A',
                  fontFamily: SERIF, fontSize: 10,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  padding: '5px 11px', borderRadius: 999,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
                }}
              >✦ Coming Soon</span>
            )}
            <BrandedPlaceholder label={b.name} aspectRatio="4 / 3" imageUrl={b.image_url} testId={`booth-image-${b.slug}`} />
            <div style={{ padding: '22px 22px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <h4 style={{
                  fontFamily: SERIF, fontWeight: 500, fontSize: 18,
                  color: IVORY, margin: 0, letterSpacing: '-0.005em',
                }}>{b.name}</h4>
                {b.flagship && !b.coming_soon && (
                  <span data-testid={`booth-flagship-${b.slug}`} style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: GOLD,
                  }}>Flagship ✦</span>
                )}
              </div>
              <p style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13, color: IVORY_DIM, lineHeight: 1.55,
                margin: '0 0 12px',
              }}>{b.tagline}</p>
              {Number(b.price_from) > 0 && (
                <div
                  data-testid={`booth-price-${b.slug}`}
                  style={{
                    fontFamily: SERIF, fontSize: 13.5,
                    color: GOLD, letterSpacing: '0.04em',
                    margin: '0 0 16px',
                  }}
                >
                  From <span style={{ fontSize: 18, fontWeight: 500 }}>£{b.price_from}</span>
                </div>
              )}
              <div style={{ marginTop: 'auto' }}>
                {b.coming_soon ? (
                  <button
                    type="button"
                    onClick={() => openEnquiry('')}
                    data-testid={`booth-cta-${b.slug}`}
                    style={{
                      ...PILL_BUTTON_PRIMARY,
                      padding: '10px 20px', fontSize: 13,
                      letterSpacing: '0.04em',
                    }}
                  >Enquire &amp; book early ✦</button>
                ) : (
                  <button
                    type="button"
                    onClick={() => openEnquiry('')}
                    data-testid={`booth-cta-${b.slug}`}
                    style={{
                      background: 'transparent', border: 'none',
                      color: GOLD, fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 13, letterSpacing: '0.04em',
                      padding: 0, cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationColor: GOLD_FAINT,
                      textUnderlineOffset: 4,
                    }}
                  >Enquire about this booth ✦</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Recently captured (iter220) ──────────────────────────────────
// A quiet showcase of the last 3 published events. The strip auto-
// hides when there are no public events yet, so a fresh deploy
// never shows an empty section. As Craig publishes more events at
// /admin/event-galleries, this strip becomes evergreen social
// proof for the commercial vertical with zero ongoing maintenance.

const RecentlyCaptured = ({ events }) => {
  if (!events || events.length === 0) return null;
  return (
    <section
      data-testid="photobooth-recently-captured"
      style={{
        padding: 'clamp(70px, 10vw, 110px) clamp(20px, 5vw, 60px)',
        borderTop: `1px solid ${GOLD_HAIRLINE}`,
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <SectionEyebrow>✦ Recently captured</SectionEyebrow>
        <SectionHeading>Nights we&apos;ve already lit</SectionHeading>
        <SectionLede>
          A glimpse of evenings already captured. Each one started with a single enquiry.
        </SectionLede>

        <div style={{
          display: 'grid', gap: 20, marginTop: 50,
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        }}>
          {events.map((ev) => (
            <Link
              key={ev.slug}
              to={`/events/${ev.slug}`}
              data-testid={`recent-event-${ev.slug}`}
              style={{
                background: WALNUT_CARD,
                border: `1px solid ${GOLD_HAIRLINE}`,
                borderRadius: 12,
                overflow: 'hidden',
                textDecoration: 'none', color: IVORY,
                display: 'flex', flexDirection: 'column',
                transition: 'transform 320ms ease, border-color 320ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = GOLD;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = GOLD_HAIRLINE;
              }}
            >
              <BrandedPlaceholder
                label={ev.name}
                sublabel={ev.venue_name}
                aspectRatio="4 / 3"
                imageUrl={mediaUrl(ev.image_url)}
              />
              <div style={{ padding: '20px 22px 22px' }}>
                <h4 style={{
                  fontFamily: SERIF, fontWeight: 500, fontSize: 18,
                  margin: '0 0 4px', letterSpacing: '-0.005em',
                }}>{ev.name}</h4>
                <div style={{
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12.5, color: IVORY_DIM,
                  letterSpacing: '0.04em',
                }}>
                  {ev.venue_name}{ev.date ? ` · ${ev.date}` : ''}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <Link
            to="/events"
            data-testid="recently-captured-see-all"
            style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13.5, color: GOLD,
              textDecoration: 'underline',
              textDecorationColor: GOLD_FAINT,
              textUnderlineOffset: 4,
              letterSpacing: '0.04em',
            }}
          >See every night we&apos;ve captured ✦</Link>
        </div>
      </div>
    </section>
  );
};

// ─── Social proof ─────────────────────────────────────────────────
// Real numbers, quietly stated. Update STATS as the catalogue
// grows — these live in code (not Mongo) so a typo can't ship
// without a code review.
const SOCIAL_STATS = [
  { value: '120+', label: 'Weddings captured' },
  { value: '40+',  label: 'Proms attended' },
  { value: '50+',  label: 'Venues across the region' },
];
const SocialProof = () => (
  <section
    data-testid="photobooth-social-proof"
    style={{
      padding: 'clamp(60px, 9vw, 100px) clamp(20px, 5vw, 60px)',
      borderTop: `1px solid ${GOLD_HAIRLINE}`,
      textAlign: 'center',
    }}
  >
    <p style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 'clamp(18px, 2.4vw, 24px)',
      color: IVORY, maxWidth: 780, margin: '0 auto',
      lineHeight: 1.5,
    }} data-testid="social-proof-statement">
      Trusted by schools, wedding venues and event organisers across
      North&nbsp;Wales, Cheshire and the North&nbsp;West.
    </p>
    <div style={{
      marginTop: 44,
      display: 'grid', gap: 22,
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      maxWidth: 820, margin: '44px auto 0',
    }}>
      {SOCIAL_STATS.map((s) => (
        <div key={s.label} data-testid={`social-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} style={{
          padding: '26px 18px',
          background: WALNUT_CARD,
          border: `1px solid ${GOLD_HAIRLINE}`,
          borderRadius: 12,
        }}>
          <div style={{
            fontFamily: SERIF, fontWeight: 600, color: GOLD,
            fontSize: 'clamp(28px, 4vw, 38px)', letterSpacing: '-0.01em',
            marginBottom: 6,
          }}>{s.value}</div>
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, color: IVORY_DIM, letterSpacing: '0.04em',
          }}>{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

// ─── Coverage strip ───────────────────────────────────────────────
// iter222i — coverage regions that have a dedicated SEO landing
// page become clickable links; the rest stay as quiet info pills.
const REGIONAL_PAGES = {
  'cheshire':    '/photobooth-hire-cheshire',
  'north wales': '/photobooth-hire-north-wales',
  'liverpool':   '/photobooth-hire-liverpool',
  'manchester':  '/photobooth-hire-manchester',
  'wirral':      '/photobooth-hire-wirral',
  'shropshire':  '/photobooth-hire-shropshire',
  'lancashire':  '/photobooth-hire-lancashire',
  'merseyside':  '/photobooth-hire-merseyside',
};
const CoverageStrip = () => (
  <section
    data-testid="photobooth-coverage"
    style={{
      padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
      borderTop: `1px solid ${GOLD_HAIRLINE}`,
      textAlign: 'center',
    }}
  >
    <SectionEyebrow>✦ Coverage Area</SectionEyebrow>
    <SectionHeading>Where we travel</SectionHeading>
    <div style={{
      display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
      gap: 10, maxWidth: 720, margin: '30px auto 0',
    }}>
      {COVERAGE_REGIONS.map(r => {
        const target = REGIONAL_PAGES[r.toLowerCase()];
        const testid = `coverage-${r.toLowerCase().replace(/ /g, '-')}`;
        const baseStyle = {
          padding: '8px 18px', borderRadius: 999,
          border: `1px solid ${target ? GOLD : GOLD_FAINT}`,
          background: target ? 'rgba(212,170,82,0.14)' : 'rgba(212,170,82,0.06)',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13, color: target ? '#FFE7A8' : IVORY,
          letterSpacing: '0.04em', textDecoration: 'none',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          transition: 'border-color 320ms ease, background 320ms ease',
        };
        return target ? (
          <Link
            key={r}
            to={target}
            data-testid={testid}
            style={baseStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,170,82,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212,170,82,0.14)'; }}
          >{r} <span style={{ color: GOLD, fontStyle: 'normal' }}>→</span></Link>
        ) : (
          <span key={r} data-testid={testid} style={baseStyle}>{r}</span>
        );
      })}
    </div>
  </section>
);

// ─── Booking CTA strip ────────────────────────────────────────────
const BookingCTA = ({ openEnquiry }) => (
  <section
    data-testid="photobooth-final-cta"
    style={{
      padding: 'clamp(64px, 9vw, 110px) clamp(20px, 5vw, 60px)',
      textAlign: 'center',
      borderTop: `1px solid ${GOLD_HAIRLINE}`,
      background: 'radial-gradient(900px 500px at 50% 0%, rgba(212,170,82,0.06) 0%, transparent 60%)',
    }}
  >
    <h2 style={{
      fontFamily: SERIF, fontWeight: 500,
      fontSize: 'clamp(26px, 4vw, 40px)', color: IVORY,
      margin: 0, letterSpacing: '-0.01em',
    }}>Ready to create unforgettable memories?</h2>
    <p style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 'clamp(14px, 1.4vw, 17px)', color: IVORY_DIM,
      margin: '18px auto 30px', maxWidth: 540, lineHeight: 1.55,
    }}>
      Tell us about your evening and we&apos;ll confirm availability with a quote.
    </p>
    <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={() => openEnquiry('')}
        data-testid="final-cta-check-availability"
        style={PILL_BUTTON_PRIMARY}
      >Check Availability ✦</button>
      <a
        href="mailto:hello@eventdrop.co"
        data-testid="final-cta-contact"
        style={PILL_BUTTON_GHOST}
      >Contact Us</a>
    </div>
  </section>
);

// ─── Shared section primitives ────────────────────────────────────
const SectionEyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
    color: GOLD_MUTED, marginBottom: 14, textAlign: 'center',
  }}>{children}</div>
);
const SectionHeading = ({ children }) => (
  <h2 style={{
    fontFamily: SERIF, fontWeight: 500,
    fontSize: 'clamp(28px, 4.5vw, 44px)', color: IVORY,
    textAlign: 'center', margin: 0, letterSpacing: '-0.015em',
    lineHeight: 1.1,
  }}>{children}</h2>
);
const SectionLede = ({ children }) => (
  <p style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 'clamp(14px, 1.4vw, 17px)', color: IVORY_DIM,
    maxWidth: 620, margin: '18px auto 0', textAlign: 'center', lineHeight: 1.6,
  }}>{children}</p>
);

const Footer = () => (
  <footer style={{
    padding: '48px 24px',
    borderTop: `1px solid ${GOLD_HAIRLINE}`,
    textAlign: 'center',
  }}>
    <div style={{ fontFamily: SERIF, fontSize: 20, color: IVORY, letterSpacing: '-0.005em' }}>
      EventDrop <span style={{ color: GOLD, fontSize: 14 }}>✦</span>
    </div>
    <div style={{
      marginTop: 10, fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, color: IVORY_FAINT,
    }}>
      Photobooth Hire across North Wales, Cheshire, Shropshire &amp; the North West · © {new Date().getFullYear()}
    </div>
  </footer>
);

export default PhotoboothHirePage;
